import type { Entity } from 'playcanvas'
import { useArenaStore } from '../store'
import { getColliders, moveAndResolveCollisions, MAX_STEP } from './colliders'
import {
  MovementFeel,
  stepHorizontalVelocity,
  clampParam,
  WALK_ACCEL,
  STOP_DECEL,
  SPRINT_ACCEL,
  AIR_ACCEL_MULT,
} from './feel/movement'

// 6 m/s was Half-Life 2's SPRINT speed — in a 36m room that reads as a
// hovering camera, not a person. Real preferred walking pace is ~1.3 m/s;
// games run ~2x that because the player has no proprioception.
const MOVE_SPEED   = 2.6
const SPRINT_MULT  = 1.77 // -> 4.6 m/s, a jog
// Real gravity feels floaty in first person (no body to anchor the camera);
// every shipped FPS uses 1.5-2x. Jump apex is now ~0.45m — a human hop.
const GRAVITY      = 18
const JUMP_SPEED   = 4.0
const EYE_HEIGHT   = 1.65
const RADIUS       = 0.3
const HEIGHT       = 1.8
const SENSITIVITY  = 0.002
const RAD_TO_DEG   = 180 / Math.PI

// A single mouse frame reporting a huge delta is never a real flick — it's a
// pointer-lock re-centre, an alt-tab, or an OS gesture. Swallow it rather than
// snapping the view to the moon.
const LOOK_SPIKE_PX = 250

// Grace windows that make jumping forgiving instead of frame-perfect:
//  - coyote: you can still jump for a moment AFTER walking off an edge.
//  - buffer: a jump pressed just BEFORE landing fires the instant you touch down.
const COYOTE_TIME       = 0.12 // seconds
const JUMP_BUFFER_TIME  = 0.12 // seconds

/**
 * Accessibility intensity for all added camera motion (bob, sway, roll, breath,
 * FOV kick). Defaults from prefers-reduced-motion; movement itself is unaffected.
 */
function readMotionScale(): number {
  try {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0
    }
  } catch {
    // matchMedia unavailable (test env / SSR)
  }
  return 1
}

// 80 deg stopped you looking straight up at the ceiling fixtures, which are
// a deliberate part of the scene.
export const PITCH_MAX = Math.PI * 89 / 180

export function clampPitch(p: number): number {
  return Math.max(-PITCH_MAX, Math.min(PITCH_MAX, p))
}

interface Keys {
  forward: boolean; backward: boolean
  left: boolean; right: boolean
  jump: boolean; sprint: boolean
}

export class PlayerController {
  private pos  = { x: 0, y: 0.1, z: 8 }
  private vel  = { x: 0, y: 0,   z: 0 }
  private yaw   = 0
  private pitch = 0
  private keys: Keys = {
    forward: false, backward: false,
    left: false, right: false,
    jump: false, sprint: false,
  }
  private grounded = false
  private walking  = false
  private sprinting = false

  // Jump forgiveness state.
  private coyoteTimer = COYOTE_TIME + 1 // start "expired" (airborne at spawn)
  private jumpBuffer  = 0

  private readonly motionScale = readMotionScale()
  private readonly feel = new MovementFeel()

  /** Set to true while the intro sweep controls the camera. */
  introActive = false

  constructor(private readonly cameraEntity: Entity) {}

  /** Move the player instantly (used by building transitions). */
  teleport(x: number, z: number, yawDeg?: number): void {
    this.pos.x = x
    this.pos.z = z
    this.vel.x = 0
    this.vel.z = 0
    if (yawDeg !== undefined) this.yaw = (yawDeg * Math.PI) / 180
  }

  /**
   * Full-pose teleport reserved for deterministic browser acceptance views.
   * Normal world transitions intentionally use the floor-preserving method
   * above; this one also resets vertical motion and look pitch.
   */
  teleport3d(
    x: number,
    y: number,
    z: number,
    yawDeg: number,
    pitchDeg = 0,
  ): void {
    this.pos = { x, y, z }
    this.vel = { x: 0, y: 0, z: 0 }
    this.yaw = (yawDeg * Math.PI) / 180
    this.pitch = (pitchDeg * Math.PI) / 180
    this.grounded = false
    this.walking = false
    this.sprinting = false
  }

  /**
   * Where to put the player if they ever end up outside the world. Set once at
   * boot; without it a single gap in the collision geometry means an endless
   * fall with no recovery but a page reload.
   */
  private recoveryPoint: { x: number; z: number; yaw: number } | null = null

  setRecoveryPoint(x: number, z: number, yawDeg: number): void {
    this.recoveryPoint = { x, z, yaw: yawDeg }
  }

  getPos(): { x: number; y: number; z: number } {
    return { x: this.pos.x, y: this.pos.y, z: this.pos.z }
  }

  getYaw(): number { return this.yaw }

  // ── Event handlers stored as arrow functions for clean removal ──

  private readonly onMouseMove = (e: MouseEvent): void => {
    if (document.pointerLockElement === null) return
    // Reject single-frame spikes (pointer-lock re-centre / alt-tab / OS gesture).
    if (Math.abs(e.movementX) > LOOK_SPIKE_PX || Math.abs(e.movementY) > LOOK_SPIKE_PX) return
    const { mouseSensitivity } = useArenaStore.getState()
    const sens = SENSITIVITY * mouseSensitivity
    this.yaw   += e.movementX * sens
    this.pitch -= e.movementY * sens
    this.pitch  = clampPitch(this.pitch)
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    switch (e.code) {
      case 'KeyW': case 'ArrowUp':             this.keys.forward  = true; break
      case 'KeyS': case 'ArrowDown':           this.keys.backward = true; break
      case 'KeyA': case 'ArrowLeft':           this.keys.left     = true; break
      case 'KeyD': case 'ArrowRight':          this.keys.right    = true; break
      case 'Space':
        // Buffer only the press EDGE — key-repeat must not keep re-arming it.
        if (!this.keys.jump) this.jumpBuffer = JUMP_BUFFER_TIME
        this.keys.jump = true
        break
      case 'ShiftLeft': case 'ShiftRight':     this.keys.sprint   = true; break
    }
  }

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    switch (e.code) {
      case 'KeyW': case 'ArrowUp':             this.keys.forward  = false; break
      case 'KeyS': case 'ArrowDown':           this.keys.backward = false; break
      case 'KeyA': case 'ArrowLeft':           this.keys.left     = false; break
      case 'KeyD': case 'ArrowRight':          this.keys.right    = false; break
      case 'Space':                            this.keys.jump     = false; break
      case 'ShiftLeft': case 'ShiftRight':     this.keys.sprint   = false; break
    }
  }

  init(): void {
    document.addEventListener('mousemove', this.onMouseMove)
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup',   this.onKeyUp)
  }

  update(dt: number): void {
    const { isPlaying, viewBobEnabled } = useArenaStore.getState()
    const active = isPlaying && !this.introActive
    const wasGrounded = this.grounded

    // Decay the jump buffer regardless of state so a stale press expires.
    this.jumpBuffer = Math.max(0, this.jumpBuffer - dt)

    // Gravity
    this.vel.y -= GRAVITY * dt

    // ── horizontal: ramp velocity toward the target (accel/decel) ──────────
    if (active) {
      const targetSpeed = MOVE_SPEED * (this.keys.sprint ? SPRINT_MULT : 1)
      const sinYaw = Math.sin(this.yaw)
      const cosYaw = Math.cos(this.yaw)

      let mx = 0, mz = 0
      if (this.keys.forward)  { mx += sinYaw; mz -= cosYaw }
      if (this.keys.backward) { mx -= sinYaw; mz += cosYaw }
      if (this.keys.left)     { mx -= cosYaw; mz -= sinYaw }
      if (this.keys.right)    { mx += cosYaw; mz += sinYaw }

      const len = Math.sqrt(mx * mx + mz * mz)
      this.walking   = len > 0
      this.sprinting = this.keys.sprint && len > 0

      let tx = 0, tz = 0
      if (len > 0) {
        tx = (mx / len) * targetSpeed
        tz = (mz / len) * targetSpeed
      }

      // Instant-stop targets decelerate hard; a moving target accelerates
      // (gentler when building to a sprint); air gives far less authority.
      let accel = len > 0 ? (this.keys.sprint ? SPRINT_ACCEL : WALK_ACCEL) : STOP_DECEL
      if (!this.grounded) accel *= AIR_ACCEL_MULT

      const nv = stepHorizontalVelocity(this.vel.x, this.vel.z, tx, tz, accel, dt)
      this.vel.x = nv.x
      this.vel.z = nv.z
    } else {
      // Coast to a stop rather than freezing mid-stride (e.g. on pause).
      const nv = stepHorizontalVelocity(this.vel.x, this.vel.z, 0, 0, STOP_DECEL, dt)
      this.vel.x = nv.x
      this.vel.z = nv.z
      this.walking   = false
      this.sprinting = false
    }

    // Movement-based, stair-aware resolution. It integrates the delta from the
    // known-good start pose internally (so we do NOT pre-add velocity here), and
    // uses the travel direction + previous grounded state to climb risers on the
    // way up and settle onto lower treads on the way down.
    const result = moveAndResolveCollisions(
      this.pos,
      { x: this.vel.x * dt, y: this.vel.y * dt, z: this.vel.z * dt },
      RADIUS, HEIGHT, getColliders(),
      { wasGrounded, maxStepUp: MAX_STEP, maxStepDown: MAX_STEP },
    )
    // Out of bounds: below the world means a hole in the collision geometry.
    // Rather than fall forever, put the player back at the recovery point —
    // this turns any future geometry leak from fatal into merely surprising.
    if (this.recoveryPoint && result.y < -20) {
      this.teleport(this.recoveryPoint.x, this.recoveryPoint.z, this.recoveryPoint.yaw)
      this.vel.y = 0
      this.pos.y = 0.1
      return
    }

    this.pos.x = result.x
    this.pos.y = result.y
    this.pos.z = result.z
    this.grounded = result.grounded

    // Cancel upward velocity when the head hits a ceiling/lintel while rising.
    if (result.hitCeiling && this.vel.y > 0) this.vel.y = 0

    // Coyote timer: 0 on solid ground, counting up once airborne.
    if (result.grounded) this.coyoteTimer = 0
    else                 this.coyoteTimer += dt

    // Capture the touchdown impact (downward speed) BEFORE we zero it, so the
    // landing dip can scale with how hard we hit.
    const landed = result.grounded && !wasGrounded && this.vel.y < 0
    const landingImpact = landed ? -this.vel.y : 0
    if (result.grounded && this.vel.y < 0) this.vel.y = 0

    // Jump: fires on a buffered press while grounded OR within the coyote
    // window, and only when we aren't already rising.
    const canJump = result.grounded || this.coyoteTimer <= COYOTE_TIME
    if (active && this.jumpBuffer > 0 && canJump && this.vel.y <= 0.0001) {
      this.vel.y = JUMP_SPEED
      this.jumpBuffer = 0
      this.coyoteTimer = COYOTE_TIME + 1 // consume the grace so we can't double-jump
    }

    if (!this.introActive) {
      const hSpeed = Math.sqrt(this.vel.x * this.vel.x + this.vel.z * this.vel.z)
      const feel = this.feel.update(dt, {
        isWalking:   this.walking,
        isSprinting: this.sprinting,
        isGrounded:  this.grounded,
        bobEnabled:  viewBobEnabled,
        speed:       hSpeed,
        motionScale: this.motionScale,
        landingImpact,
      })

      // Rotation FIRST — so entity.right reflects the current roll before we
      // read it to place the camera-LOCAL lateral bob offset.
      const pitchDeg = clampParam(this.pitch * RAD_TO_DEG, -PITCH_MAX * RAD_TO_DEG, PITCH_MAX * RAD_TO_DEG)
      this.cameraEntity.setEulerAngles(
        pitchDeg,
        -this.yaw * RAD_TO_DEG + feel.yawOffsetDeg,
        feel.rollDeg,
      )
      const right = this.cameraEntity.right
      this.cameraEntity.setPosition(
        this.pos.x + right.x * feel.lateralOffset,
        this.pos.y + EYE_HEIGHT + feel.yOffset,
        this.pos.z + right.z * feel.lateralOffset,
      )
      // Update camera FOV for the speed-driven widen.
      const cam = this.cameraEntity.camera as ({ fov?: number } | null | undefined)
      if (cam) cam.fov = feel.fov
    }
  }

  destroy(): void {
    document.removeEventListener('mousemove', this.onMouseMove)
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup',   this.onKeyUp)
    // NB: the player does NOT clear the collider registry — it doesn't own it.
    // The scene builds the world, so the scene tears it down (PcCanvas cleanup).
  }
}
